import type { Processor } from '@app/store/middleware/processorMiddleware.js';
import {
  elevationChartClose,
  elevationChartSetTrackGeojson,
  elevationSetSettings,
} from '@features/elevationChart/model/actions.js';
import { isPremium } from '@features/premium/premium.js';
import { readElevationSources } from '@shared/elevation.js';
import { trackMatomo } from '@shared/trackMatomo.js';
import { transportTypeDefs } from '@shared/transportTypeDefs.js';
import { lineString } from '@turf/helpers';
import {
  routePlannerSetActiveAlternativeIndex,
  routePlannerToggleElevationChart,
} from '../actions.js';
import { ensureRouteRenderGeojson } from '../ensureRouteRenderGeojson.js';

export const routePlannerToggleElevationChartProcessor: Processor<
  | typeof routePlannerToggleElevationChart
  | typeof routePlannerSetActiveAlternativeIndex
  | typeof elevationSetSettings
> = {
  actionCreator: [
    routePlannerToggleElevationChart,
    routePlannerSetActiveAlternativeIndex,
    elevationSetSettings,
  ],
  handle: async ({ dispatch, getState, action }) => {
    // Changed elevation settings re-derive an open chart. Whichever tool is in
    // use owns it, so stand aside when it isn't this one.
    if (
      elevationSetSettings.match(action) &&
      !getState().main.tools.includes('route-planner')
    ) {
      return;
    }

    const shown = Boolean(getState().elevationChart.elevationProfilePoints);

    const toggling = routePlannerToggleElevationChart.match(action);

    if (toggling && shown) {
      dispatch(elevationChartClose());
    } else if ((!shown && toggling) || (shown && !toggling)) {
      trackMatomo(['trackEvent', 'RoutePlanner', 'toggleElevationChart']);

      // Build the densified DEM render line so the chart isn't a coarse
      // straight-segment profile; a failure (e.g. offline) falls back to the
      // route's own coordinates.
      await ensureRouteRenderGeojson(getState, dispatch).catch(() => undefined);

      const { alternatives, activeAlternativeIndex, renderGeojson, points } =
        getState().routePlanner;

      const alternative = alternatives[activeAlternativeIndex];

      const fallbackCoords = alternative
        ? alternative.legs
            .flatMap((leg) => leg.steps)
            .flatMap((step) => step.geometry.coordinates)
        : [];

      const feature =
        renderGeojson ??
        (fallbackCoords.length >= 2 ? lineString(fallbackCoords) : null);

      if (!feature) {
        return;
      }

      // Mark the intermediate route points (midpoints) along the profile; the
      // start and finish are the chart's own endpoints, so they're omitted.
      const midpoints = points
        .slice(1, -1)
        .map(({ lat, lon }) => ({ lat, lon }));

      // Which model the profile's elevation belongs to: premium overrides every
      // vertex from our terrain model, and a router that returns none at all
      // (manual, OSRM) has its coordinates filled from it too — only
      // GraphHopper's own values, kept on the free tier, are SRTM. The sources
      // ride on the render line, so they survive its cache being reused.
      const provenance =
        isPremium(getState().auth.user) ||
        transportTypeDefs[getState().routePlanner.transportType].api !== 'gh'
          ? 'terrain-model'
          : 'srtm';

      // Render the line's coordinates as-is rather than resampling a fresh
      // server profile.
      dispatch(
        elevationChartSetTrackGeojson(feature, true, midpoints, {
          provenance,
          sources: readElevationSources(feature),
        }),
      );
    }
  },
};
